// components/UserLevelProgress.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  LinearProgress,
  Avatar,
  Chip,
  Tooltip
} from '@mui/material';
import { motion } from 'framer-motion';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import generalApi from '../../../generalApi';

const UserLevelProgress = ({ userId, deckId, cardStyle }) => {
  const [loading, setLoading] = useState(true);
  const [xpData, setXpData] = useState({
    currentXP: 0,
    currentLevel: 1,
    xpToNextLevel: 100,
    levelTitle: 'Novice I',
    progress: 0
  });
  const [levelXpData, setLevelXpData] = useState({
    xpRequired: 0,
    title: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!userId || !deckId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // First API call to get user-specific XP data
        const userXpResponse = await generalApi.get(`/xp/user-deck/${userId}/${deckId}`);
        
        if (userXpResponse.data.success) {
          console.log('[user xp data] ', userXpResponse.data.data);
          const userData = userXpResponse.data.data;
          setXpData(userData);
          
          // Second API call to get level-specific XP data
          const levelResponse = await generalApi.get(`/xp/${userData.currentLevel}`);
          
          if (levelResponse.data.success) {
            console.log('[level xp data] ', levelResponse.data.data);
            setLevelXpData(levelResponse.data.data);

            console.log('UserData ', userData);
            
            // Calculate progress as the difference between required XP and XP to next level
            const totalXpRequired = levelResponse.data.data.xpRequired;
            const xpToNextLevel = userData.xpToNextLevel;
            const progress = totalXpRequired - xpToNextLevel; // How much XP has been earned so far
            const updatedProgress = Math.min(
              Math.round((progress / totalXpRequired) * 100),
              100
            );
            
            setXpData(prev => ({
              ...prev,
              progress: updatedProgress
            }));
          } else {
            console.error('Failed to fetch level XP data:', levelResponse.data.message);
          }
        } else {
          console.error('Failed to fetch user XP data:', userXpResponse.data.message);
        }
      } catch (error) {
        console.error('Error fetching XP data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userId, deckId]);

  // Determine level color based on level
  const getLevelColor = (level) => {
    if (level <= 2) return '#4caf50'; // Green - beginner
    if (level <= 5) return '#2196f3'; // Blue - intermediate
    if (level <= 8) return '#ff9800'; // Orange - advanced
    return '#9c27b0'; // Purple - expert
  };

  const levelColor = getLevelColor(xpData.currentLevel);

  return (
    <Card sx={{ ...cardStyle }}>
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar sx={{ bgcolor: levelColor, mr: 1.5, width: 32, height: 32 }}>
              <EmojiEventsIcon sx={{ fontSize: "1.2rem" }} />
            </Avatar>
            <Box>
              <Typography variant="subtitle1" fontWeight="600" lineHeight={1.2}>
                Level Progress
              </Typography>
              <Typography variant="caption" color="text.secondary">
                <span style={{ fontWeight: 'medium' }}>{levelXpData.title || xpData.levelTitle}</span>
              </Typography>
            </Box>
          </Box>
          <Tooltip title={`${levelXpData.xpRequired ? (levelXpData.xpRequired - xpData.xpToNextLevel) : 0}/${levelXpData.xpRequired || xpData.xpToNextLevel} XP to next level`}>
            <Chip 
              label={`Level ${xpData.currentLevel}`} 
              size="small" 
              sx={{ 
                bgcolor: levelColor,
                color: 'white',
                fontWeight: 'bold' 
              }} 
            />
          </Tooltip>
        </Box>

        <Box sx={{ mt: 1.5, mb: 0.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" fontWeight="medium">
              Current XP
            </Typography>
            <Typography variant="caption" fontWeight="medium">
              {levelXpData.xpRequired ? (levelXpData.xpRequired - xpData.xpToNextLevel) : 0}/{levelXpData.xpRequired || xpData.xpToNextLevel} XP
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={xpData.progress}
            sx={{
              height: 8,
              borderRadius: 3,
              mb: 1,
              bgcolor: `${levelColor}33`, // Add transparency to base color
              '& .MuiLinearProgress-bar': {
                bgcolor: levelColor
              }
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
          <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
            {100- xpData.progress}% to Level {xpData.currentLevel + 1}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default UserLevelProgress;